import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { CountriesService } from '../../../services/countries/countries.service';

@Component({
  selector: 'app-registration',
  imports: [ReactiveFormsModule, CommonModule, RouterModule, FormsModule],
  standalone: true,
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.css',
})
export class RegistrationComponent implements OnInit {
  // --- Form Groups ---
  registrationForm!: FormGroup;
  otpForm!: FormGroup;
  currentScreen: number = 1;
  isSubmitting: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  selectedCountryCode: string = '+91';
  countries: any[] = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toaster: ToastrService,
    private countriesService: CountriesService,
  ) {}

  ngOnInit(): void {
    this.countries = this.countriesService.getAllCountries();

    const PASSWORD_REGEX =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
    this.registrationForm = this.fb.group(
      {
        username: [
          '',
          [
            Validators.required,
            Validators.minLength(4),
            Validators.pattern(/^[a-zA-Z0-9]+$/),
          ],
        ],
        email: ['', [Validators.required, Validators.email]],
        mobileNumber: [
          '',
          [Validators.required, Validators.pattern(/^[0-9]{4,15}$/)],
        ], // Flexible validation: 4-15 digits
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(PASSWORD_REGEX),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validator: this.passwordMatchValidator,
      },
    );

    this.otpForm = this.fb.group({
      digit1: ['', [Validators.required]],
      digit2: ['', [Validators.required]],
      digit3: ['', [Validators.required]],
      digit4: ['', [Validators.required]],
    });
  }

  // === CUSTOM VALIDATORS ===
  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      form.get('confirmPassword')?.setErrors({ passwordMismatch: true });
    } else {
      form.get('confirmPassword')?.setErrors(null);
    }
  }

  // === OTP AUTO FOCUS HANDLER ===
  autoFocusNext(event: any, nextElement: HTMLInputElement | null): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length === 1) {
      if (nextElement) {
        nextElement.focus();
      }
    }
  }

  // === API SERVICE CALLS ===
  registerUser(): void {
    if (this.registrationForm.invalid) {
      console.log('Form is invalid. Cannot proceed.');
      this.registrationForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.authService
      .sendRegistrationOtp({
        username: this.registrationForm.get('username')?.value,
        email: this.registrationForm.get('email')?.value,
        mobile_number:
          this.selectedCountryCode +
          this.registrationForm.get('mobileNumber')?.value,
      })
      .subscribe({
        next: (data) => {
          this.currentScreen = 2;
          this.isSubmitting = false;
          this.toaster.success(data.message_key);
          console.log(data.data);
        },
        error: (error) => {
          this.toaster.error(error.error.message_key || 'Something went wrong');
          this.isSubmitting = false;
        },
      });
  }

  nextScreen(): void {
    if (this.currentScreen === 1) {
      this.registerUser();
    } else if (this.currentScreen === 2) {
      if (this.otpForm.valid) {
        this.isSubmitting = true;

        let otp =
          this.otpForm.get('digit1')?.value +
          this.otpForm.get('digit2')?.value +
          this.otpForm.get('digit3')?.value +
          this.otpForm.get('digit4')?.value;

        this.authService
          .verifyRegistrationOtp({
            mobile_number:
              this.selectedCountryCode +
              this.registrationForm.get('mobileNumber')?.value,
            code: otp,
          })
          .subscribe({
            next: (data) => {
              this.isSubmitting = true;

              this.authService
                .submitRegistration({
                  username: this.registrationForm.get('username')?.value,
                  password: this.registrationForm.get('password')?.value,
                  mobileNumber:
                    this.selectedCountryCode +
                    this.registrationForm.get('mobileNumber')?.value,
                  selectedFiles: [],
                  email: this.registrationForm.get('email')?.value,
                })
                .subscribe({
                  next: (submitData) => {
                    this.currentScreen = 3;
                    this.isSubmitting = false;
                    this.toaster.success(submitData.message_key);
                  },
                  error: (error) => {
                    this.toaster.error(
                      error.error.message_key || 'Something went wrong',
                    );
                    this.isSubmitting = false;
                  },
                });
            },
            error: (error) => {
              this.toaster.error(
                error.error.message_key || 'Something went wrong',
              );
              this.isSubmitting = false;
            },
          });
      } else {
        console.log('OTP form is incomplete.');
        this.otpForm.markAllAsTouched();
      }
    }
  }

  previousScreen(): void {
    if (this.currentScreen > 1) {
      this.currentScreen--;
      console.log('Returned to Screen:', this.currentScreen);
    }
  }

  backToRegistration(): void {
    this.currentScreen = 1;
    this.otpForm.reset();
  }

  resendOtp(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.authService
      .sendRegistrationOtp({
        username: this.registrationForm.get('username')?.value,
        email: this.registrationForm.get('email')?.value,
        mobile_number:
          this.selectedCountryCode +
          this.registrationForm.get('mobileNumber')?.value,
      })
      .subscribe({
        next: (data) => {
          this.isSubmitting = false;
          this.toaster.success(data.message_key || 'OTP resent successfully');
        },
        error: (error) => {
          this.isSubmitting = false;
          this.toaster.error(
            error.error.message_key ||
              'Failed to resend OTP. Please try again.',
          );
        },
      });
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
